import { getGlobalHeadersAsObject } from "../global-headers";
import type { ApplicantReplyDraft } from "./enrich-pitch";

/**
 * Reminders after an unanswered personally drafted pitch (ticket #168).
 *
 * The automatic follow-up chain stops after a message the recruiter wrote or
 * approved themselves, so these four endpoints are the manual replacement: who
 * is due a reminder, what it should say, sending it, and declining it for good.
 * The server re-checks eligibility on every write — a candidate who answered in
 * the meantime is no longer due — so nothing here has to be kept in sync.
 */

/** One candidate whose personal pitch went unanswered long enough to remind. */
export interface DueReminder {
	person_id: number;
	person_name: string;
	assignment_id: number;
	/** The pitch's own channel — the reminder goes out on this one, not another. */
	channel: "email" | "linkedin";
	pitch_sent_at: string;
	/** The prior-conversation row the pitch produced (ConversationContent id on
	 *  LinkedIn, EmailJournal id on email), so the composer can show the thread.
	 *  Null until the mailbox or LinkedIn sync has linked the sent copy back. */
	activity_id: number | null;
}

/** What a sent reminder answers with. */
export interface SentReminder {
	id: number;
	channel: "email" | "linkedin";
}

const jsonHeaders = (): Record<string, string> => ({
	...getGlobalHeadersAsObject(),
	"Content-Type": "application/json",
});

/**
 * Candidates currently owed a reminder. Pass `assignmentId` for one role's queue
 * (the reminder flow); pass neither and it spans every role the user works,
 * each row naming its own — one call for all the role cards' badges.
 */
export const fetchDueReminders = async (options: {
	assignmentId?: number;
	userId?: number;
}): Promise<DueReminder[]> => {
	const params = new URLSearchParams();
	if (options.assignmentId !== undefined) {
		params.set("assignment_id", String(options.assignmentId));
	}
	if (options.userId !== undefined) {
		params.set("user_id", String(options.userId));
	}
	const response = await fetch(
		`/api/v2/person_pitch/reminder_due/?${params.toString()}`,
		{ headers: getGlobalHeadersAsObject() }
	);
	if (!response.ok) {
		throw new Error(`Could not load due reminders (${response.status})`);
	}
	return response.json();
};

/** Due reminders per assignment id — what the role cards badge themselves with. */
export const countDueRemindersByAssignment = (
	rows: DueReminder[]
): Record<number, number> => {
	const counts: Record<number, number> = {};
	for (const row of rows) {
		counts[row.assignment_id] = (counts[row.assignment_id] ?? 0) + 1;
	}
	return counts;
};

/**
 * AI-draft the reminder to this candidate's unanswered pitch. Same response
 * shape as the other drafters, so the draft dialog reads it unmodified. The
 * channel is the pitch's own and is never sent from here — a reminder on any
 * other channel would open a second conversation instead of nudging the first.
 */
export const reminderDraft = (
	personId: number,
	assignmentId: number,
	regenerate = false,
	hint = "",
	model = ""
): Promise<ApplicantReplyDraft | { error: string }> =>
	fetch("/api/v2/person_pitch/reminder_draft/", {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify({
			person_id: personId,
			assignment_id: assignmentId,
			regenerate,
			hint,
			model,
		}),
	}).then((res) => res.json());

/** Queue the reviewed reminder. Unlike a pitch, this one goes out as sent —
 *  the recruiter read this exact text and pressed Send. */
export const sendReminder = async (params: {
	personId: number;
	assignmentId: number;
	subject: string;
	text: string;
	promptTemplateId: number | null;
	isTextEdited: boolean;
}): Promise<SentReminder> => {
	const response = await fetch("/api/v2/person_pitch/reminder_send/", {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify({
			person_id: params.personId,
			assignment_id: params.assignmentId,
			subject: params.subject,
			text: params.text,
			prompt_template_id: params.promptTemplateId,
			is_text_edited: params.isTextEdited,
		}),
	});
	if (!response.ok) {
		throw new Error(`Could not send the reminder (${response.status})`);
	}
	return response.json();
};

/** Never offer this candidate's reminder again. */
export const dismissReminder = async (
	personId: number,
	assignmentId: number
): Promise<void> => {
	const response = await fetch("/api/v2/person_pitch/reminder_dismiss/", {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify({
			person_id: personId,
			assignment_id: assignmentId,
		}),
	});
	if (!response.ok) {
		throw new Error(`Could not dismiss the reminder (${response.status})`);
	}
};
